/** Match distant extinction to the actual screen-space matte instead of a
 * constant tint that leaves opaque silhouettes against its clouds. This is
 * aerial perspective for a 2D background, not a new volumetric or contact layer.
 * Borrowed textures keep their original ownership, UV transform and colour
 * space. The existing fog distances still control depth; no alpha/depth change
 * can make foreground objects transparent or alter gameplay visibility tests. */
export function createSkyPerspectiveHaze(T) {
  const installed = new WeakSet();
  const uniforms = {
    skyHazeBackdrop: { value: null },
    skyHazeViewport: { value: new T.Vector2(1, 1) },
    skyHazeUv: { value: new T.Matrix3() },
    skyHazeEnabled: { value: 0 },
    skyHazeIntensity: { value: 1 },
    skyHazeToneMapped: { value: 0 },
  };
  function install(material) {
    if (!material?.fog || installed.has(material)) return;
    installed.add(material);
    const before = material.onBeforeCompile;
    const cacheKey = material.customProgramCacheKey.bind(material);
    const priorKey = cacheKey();
    material.onBeforeCompile = function (shader, renderer) {
      before.call(this, shader, renderer);
      if (!shader.fragmentShader.includes('#include <fog_fragment>')) return;
      Object.assign(shader.uniforms, uniforms);
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <fog_pars_fragment>',
        `#include <fog_pars_fragment>
        uniform sampler2D skyHazeBackdrop;
        uniform vec2 skyHazeViewport;
        uniform mat3 skyHazeUv;
        uniform float skyHazeEnabled;
        uniform float skyHazeIntensity;
        uniform float skyHazeToneMapped;`,
      );
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <fog_fragment>',
        `#ifdef USE_FOG
          #ifdef FOG_EXP2
            float fogFactor = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
          #else
            float fogFactor = smoothstep(fogNear, fogFar, vFogDepth);
          #endif
          vec3 haze = fogColor;
          if (skyHazeEnabled > 0.5 && fogFactor > 0.0) {
            vec2 screenUv = gl_FragCoord.xy / skyHazeViewport;
            vec2 matteUv = (skyHazeUv * vec3(screenUv, 1.0)).xy;
            vec4 matte = texture2D(skyHazeBackdrop, matteUv);
            matte.rgb *= skyHazeIntensity;
            #ifdef TONE_MAPPING
              if (skyHazeToneMapped > 0.5) matte.rgb = toneMapping(matte.rgb);
            #endif
            haze = linearToOutputTexel(matte).rgb;
          }
          gl_FragColor.rgb = mix(gl_FragColor.rgb, haze, fogFactor);
        #endif`,
      );
    };
    material.customProgramCacheKey = () => `${priorKey}:sky-matte-haze-1`;
    material.needsUpdate = true;
  }
  function prepare(scene) {
    scene.traverse((object) => {
      if (Array.isArray(object.material)) object.material.forEach(install);
      else install(object.material);
    });
  }
  return {
    prepare,
    update(scene, renderer) {
      prepare(scene);
      const background = scene.background;
      const enabled = background?.isTexture && !background.isCubeTexture;
      uniforms.skyHazeEnabled.value = enabled ? 1 : 0;
      if (!enabled) return;
      if (background.matrixAutoUpdate) background.updateMatrix();
      uniforms.skyHazeBackdrop.value = background;
      uniforms.skyHazeUv.value.copy(background.matrix);
      uniforms.skyHazeIntensity.value = scene.backgroundIntensity ?? 1;
      // Three's background path deliberately skips tone mapping for sRGB art.
      uniforms.skyHazeToneMapped.value = background.colorSpace === T.SRGBColorSpace ? 0 : 1;
      renderer.getDrawingBufferSize(uniforms.skyHazeViewport.value);
    },
    dispose() {
      uniforms.skyHazeEnabled.value = 0;
      uniforms.skyHazeBackdrop.value = null;
    },
  };
}
